import axios from 'axios';
import { ElMessage } from 'element-plus';
import {decrypt} from '@/utils/func'
import router from '@/router';

const service = axios.create({
  baseURL: window.baseURL,
  timeout: 20000
});

service.interceptors.request.use(
  config => {
    const token = 'Bearer ' + localStorage.getItem('Token')
    config.headers.Authorization = token
    return config
  }
)

// 401 刷新机制：token 过期时用 refreshToken 换新 token，重发原请求
let isRefreshing = false
let pendingQueue = []  // 刷新期间 401 的请求，刷新成功后重发

function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refreshToken')
  console.info('[refresh] refreshAccessToken 调用, refreshToken=', refreshToken)
  if (!refreshToken) return Promise.reject(new Error('无 refreshToken'))
  console.info('[refresh] 发起 /auth/refresh-token 请求, URL=', window.baseURL + '/auth/refresh-token')
  return axios.get(window.baseURL + '/auth/refresh-token', {
    params: { refreshToken },
    headers: { Authorization: 'Bearer ' + localStorage.getItem('Token') }
  }).then(res => {
    const body = res.data
    // 真实结构：{code, message, data:{accessToken, refreshToken, ...}, success, timestamp}
    const info = (body && body.data && typeof body.data === 'object') ? body.data : null
    let newToken = ''
    let newRefresh = ''
    if (info) {
      newToken = info.accessToken || info.token || ''
      newRefresh = info.refreshToken || info.refresh_token || ''
    } else if (typeof body === 'string') {
      newToken = body
    } else if (body && typeof body === 'object') {
      newToken = body.accessToken || body.token || ''
      newRefresh = body.refreshToken || body.refresh_token || ''
    }
    if (!newToken) throw new Error('刷新响应无 token')
    localStorage.setItem('Token', newToken)
    if (newRefresh) localStorage.setItem('refreshToken', newRefresh)
    if (info) {
      localStorage.setItem('roles', JSON.stringify(info.roles || []))
      localStorage.setItem('userId', info.userId || '')
      localStorage.setItem('username', info.username || '')
    }
    return newToken
  })
}

function reattachAuth(config) {
  config.headers = config.headers || {}
  config.headers.Authorization = 'Bearer ' + localStorage.getItem('Token')
  return config
}

service.interceptors.response.use(
  res => {
    if(res.data.encryption){
      const result=JSON.parse(decrypt(res.data.encryption));
      return result
    }
    if (res.data instanceof Blob) {
      return res.data
    } else if (res.data.code === 200) {
      return res.data
    } else if (res.data.code === 401) {
      const originalConfig = res.config
      console.info('[401] 触发 401 刷新, isRefreshing=', isRefreshing, 'refreshToken=', localStorage.getItem('refreshToken'))
      if (!isRefreshing) {
        isRefreshing = true
        return refreshAccessToken().then(() => {
          isRefreshing = false
          // 重发刷新期间等待的请求
          const queue = pendingQueue
          pendingQueue = []
          queue.forEach(cb => cb())
          // 重发当前 401 请求
          return service(reattachAuth(originalConfig))
        }).catch(err => {
          isRefreshing = false
          pendingQueue = []
          localStorage.removeItem('Token')
          localStorage.removeItem('refreshToken')
          ElMessage.error('登录过期')
          window.location.href = window.getSsoLoginURL()
          return Promise.reject(err)
        })
      } else {
        // 正在刷新，加入队列等待刷新完成后重发
        return new Promise((resolve, reject) => {
          pendingQueue.push(() => {
            service(reattachAuth(originalConfig)).then(resolve).catch(reject)
          })
        })
      }
    } else {
      if (res.data.code && res.data.code != 0 && res.data.msg) ElMessage.error(res.data.msg);
      return res.data
    }
  },
  err => {
    // HTTP 401：token 过期，用 refreshToken 刷新后重发原请求
    if (err.response && err.response.status === 401) {
      const originalConfig = err.config
      console.info('[401-http] HTTP 401 触发刷新, isRefreshing=', isRefreshing, 'refreshToken=', localStorage.getItem('refreshToken'))
      if (!isRefreshing) {
        isRefreshing = true
        return refreshAccessToken().then(() => {
          isRefreshing = false
          const queue = pendingQueue
          pendingQueue = []
          queue.forEach(cb => cb())
          return service(reattachAuth(originalConfig))
        }).catch(e => {
          isRefreshing = false
          pendingQueue = []
          localStorage.removeItem('Token')
          localStorage.removeItem('refreshToken')
          ElMessage.error('登录过期')
          window.location.href = window.getSsoLoginURL()
          return Promise.reject(e)
        })
      } else {
        return new Promise((resolve, reject) => {
          pendingQueue.push(() => {
            service(reattachAuth(originalConfig)).then(resolve).catch(reject)
          })
        })
      }
    }
    if (err.response && err.response.status === 424) {
      window.location.href = window.getSsoLoginURL();
    }
    ElMessage.error(err.response?.data?.msg || '网络请求失败，反馈给客服');
  }
);

const post = (url, data, config = {}) => {
  config.url = url
  config.data = data
  config.method = 'post'
  config.params = config.params || {}
  return service(config)

}

/**
* get 请求
* @param {*} url 请求链接
* @param {*} params 请求参数
* @param {*} config  额外配置
*/
const get = (url, params = {}, config = {}) => {
  config.url = url
  config.params = params
  config.params._t = new Date().getTime()
  config.method = 'get'
  return service(config)
}

const deleteOp = (url, params = {}, config = { headers: { 'http-method': "DELETE" } }) => {
  config.url = url
  config.params = params
  config.method = 'post'
  return service(config)
}
// 真正的 DELETE 方法（method=delete）；query 参数走 params，body 走 data
const del = (url, params = {}, config = {}) => {
  config.url = url
  config.params = params
  config.method = 'delete'
  return service(config)
}
const put = (url, data, config = { headers: { 'http-method': "PUT" } }) => {
  config.url = url
  config.data = data
  config.method = 'post'
  config.params = {}
  return service(config)
}
// 真正的 PUT 方法（method=put），有请求体
const realPut = (url, data, config = {}) => {
  config.url = url
  config.data = data
  config.method = 'put'
  config.params = config.params || {}
  return service(config)
}


export { get, post, deleteOp, put, del, realPut }
